import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createSupportTicket } from '@/lib/support/tickets';
import type { TicketCategory, TicketPriority } from '@/types/admin';

const CATEGORIES: TicketCategory[] = ['BILLING', 'BUG', 'FEATURE_REQUEST', 'QUESTION', 'OTHER'];
const PRIORITIES: TicketPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

export default function NewTicketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory>('QUESTION');
  const [priority, setPriority] = useState<TicketPriority>('NORMAL');
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: () => createSupportTicket({ subject, category, priority, body }),
    onSuccess: (tk) => {
      toast.success(t('support.ticketCreated'));
      navigate(`/support/tickets/${tk.id}`);
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Link to="/support" className="text-sm text-primary-700 hover:underline">← {t('support.title')}</Link>
      <h1 className="text-2xl font-bold text-gray-900">{t('support.newTicket')}</h1>

      <form
        onSubmit={(e) => { e.preventDefault(); if (subject.trim() && body.trim()) create.mutate(); }}
        className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      >
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('support.subject')}</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            required
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('support.categoryLabel')}</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{t(`support.category.${c}`)}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t('support.priorityLabel')}</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              {PRIORITIES.map((p) => <option key={p} value={p}>{t(`support.priority.${p}`)}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">{t('support.message')}</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            required
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={create.isPending || !subject.trim() || !body.trim()}
            className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {t('support.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
